import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ArrowRight, Gift } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { FREEBIES } from "@/data/content";

export const CartSheet = () => {
  const { cartOpen, setCartOpen, item, plan, total, setQty, clearCart, setCheckoutOpen } = useCart();

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent
        side="right"
        data-testid="cart-sheet"
        className="bg-obsidian border-l border-white/10 text-white w-full sm:max-w-md flex flex-col"
      >
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-white text-left">Your Cart</SheetTitle>
          <SheetDescription className="sr-only">Review the items in your cart and proceed to checkout.</SheetDescription>
        </SheetHeader>

        {!item || !plan ? (
          <div className="flex-1 grid place-items-center text-center px-6">
            <div>
              <p className="text-vmuted">Your cart is empty.</p>
              <button
                onClick={() => setCartOpen(false)}
                className="mt-4 text-gold hover:text-gold-light transition-colors"
                data-testid="cart-continue-btn"
              >
                Continue browsing
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-6 space-y-6">
              <div className="glass rounded-2xl p-5 flex gap-4">
                <img
                  src="/assets/product_tube.png"
                  alt="Veda Vitals tube"
                  className="h-20 w-20 object-cover rounded-xl bg-white/5"
                />
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-serif text-xl text-white">{plan.title}</p>
                      <p className="text-xs text-vmuted">{plan.tabs} tablets per unit</p>
                    </div>
                    <button
                      onClick={clearCart}
                      data-testid="cart-remove-btn"
                      className="text-vmuted hover:text-red-400 transition-colors"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 rounded-full border border-white/15 px-2 py-1">
                      <button
                        onClick={() => setQty(item.qty - 1)}
                        data-testid="cart-dec-btn"
                        className="h-6 w-6 grid place-items-center hover:text-gold"
                        aria-label="Decrease"
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span data-testid="cart-qty" className="w-5 text-center text-sm">
                        {item.qty}
                      </span>
                      <button
                        onClick={() => setQty(item.qty + 1)}
                        data-testid="cart-inc-btn"
                        className="h-6 w-6 grid place-items-center hover:text-gold"
                        aria-label="Increase"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="font-serif text-xl text-white">₹{plan.price * item.qty}</p>
                  </div>
                </div>
              </div>

              <div className="glass rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Gift className="h-4 w-4 text-gold" strokeWidth={1.5} />
                  <p className="text-xs uppercase tracking-[0.2em] text-gold">Included free</p>
                </div>
                <ul className="grid grid-cols-2 gap-2 text-sm text-vmuted">
                  {FREEBIES.map((f) => (
                    <li key={f}>· {f}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="border-t border-white/10 pt-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-vmuted">Total</span>
                <span data-testid="cart-total" className="font-serif text-2xl text-white">
                  ₹{total}
                </span>
              </div>
              <button
                data-testid="cart-checkout-btn"
                onClick={() => {
                  setCartOpen(false);
                  setCheckoutOpen(true);
                }}
                className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-obsidian font-medium hover:bg-gold-light transition-colors duration-300"
              >
                Secure Checkout
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
